import { Camera, FileClock, Wallet, ImagePlus } from 'lucide-react';
import type { ExpenseReceipt, ExpenseStatus } from '../../types/expense';
import { StatTile } from './StatTile';
import { ExpenseCard } from './ExpenseCard';
import { sumSubmittedForMonth } from './MyExpensesList';
import { currentMonthKey } from './format';

interface Props {
    receipts: ExpenseReceipt[];
    /** Opens the device camera directly. */
    onScanCamera: () => void;
    /** Opens the gallery / file picker. */
    onUploadImage: () => void;
    onSelect: (r: ExpenseReceipt) => void;
    onViewAll: (status: 'all' | ExpenseStatus) => void;
}

export function ExpenseHome({ receipts, onScanCamera, onUploadImage, onSelect, onViewAll }: Props) {
    const draftCount = receipts.filter(r => r.Status === 'Draft').length;
    const monthKey = currentMonthKey();
    const monthSum = sumSubmittedForMonth(receipts, monthKey, 'EUR');
    const recent = receipts.slice(0, 6);

    return (
        <div className="space-y-5">
            <div className="flex gap-3">
                <StatTile
                    icon={FileClock}
                    accent="amber"
                    label="To complete"
                    value={String(draftCount)}
                    sub="drafts"
                    onClick={draftCount > 0 ? () => onViewAll('Draft') : undefined}
                />
                <StatTile
                    icon={Wallet}
                    label="This month"
                    value={monthSum}
                    sub="submitted (EUR)"
                    onClick={() => onViewAll('all')}
                />
            </div>

            <div className="space-y-2">
                <button
                    type="button"
                    onClick={onScanCamera}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 text-base font-semibold text-white shadow-sm hover:bg-blue-700 active:scale-[0.99]"
                >
                    <Camera className="h-5 w-5" /> Scan a receipt
                </button>
                <button
                    type="button"
                    onClick={onUploadImage}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.99]"
                >
                    <ImagePlus className="h-4 w-4" /> Upload image
                </button>
            </div>

            <div>
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-700">Recent</h2>
                    {receipts.length > recent.length && (
                        <button type="button" onClick={() => onViewAll('all')} className="text-sm font-medium text-blue-600">
                            View all
                        </button>
                    )}
                </div>
                {recent.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                        No receipts yet — tap “Scan a receipt” to start.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {recent.map(r => <ExpenseCard key={r.Id} receipt={r} onClick={() => onSelect(r)} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
